import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Mail, Clock, MessageSquare, Send } from "lucide-react";

const TenUResLogo = ({ size = "text-4xl" }) => (
  <span className={`tenures-logo ${size}`}>
    Ten<span className="tenures-ur font-extrabold">UR</span>es
  </span>
);

export default function ContactUs() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success("Message sent! We'll get back to you within 48 hours.");
    setFormData({ name: "", email: "", subject: "", message: "" });
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Link to="/">
            <TenUResLogo size="text-2xl" />
          </Link>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-[#111827] hover:text-[#05D38A] font-medium"
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
            <Button 
              className="bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold"
              onClick={() => navigate('/register')}
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/" 
              className="inline-flex items-center text-gray-500 hover:text-[#05D38A] mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-[#111827] mb-4">
              We're Here to Help You Navigate.
            </h1>
            <p className="text-xl text-gray-600">
              Have a question about your TenURes Passport or interested in partnering with us? Reach out below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <Card className="bg-white border-gray-200 shadow-lg">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-[#111827]">Name</Label>
                        <Input
                          id="name"
                          placeholder="Your name"
                          className="bg-white border-gray-300 focus:border-[#05D38A]"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#111827]">Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          className="bg-white border-gray-300 focus:border-[#05D38A]"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-[#111827]">Subject</Label>
                      <Select
                        value={formData.subject}
                        onValueChange={(value) => setFormData({ ...formData, subject: value })}
                      >
                        <SelectTrigger className="bg-white border-gray-300">
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          <SelectItem value="support">Support</SelectItem>
                          <SelectItem value="partnership">Partnership Inquiry</SelectItem>
                          <SelectItem value="media">Media</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-[#111827]">Message</Label>
                      <Textarea
                        id="message"
                        placeholder="How can we help you?"
                        className="bg-white border-gray-300 focus:border-[#05D38A] min-h-[150px]"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold h-12 glow-mint"
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-[#111827] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Sidebar Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="space-y-6"
            >
              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#05D38A]/10 flex items-center justify-center shrink-0">
                      <Mail className="w-5 h-5 text-[#05D38A]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#111827] mb-1">General Inquiries</h3>
                      <p className="text-gray-600 text-sm">hello@tenures.app</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#05D38A]/10 flex items-center justify-center shrink-0">
                      <Clock className="w-5 h-5 text-[#05D38A]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#111827] mb-1">Operating Hours</h3>
                      <p className="text-gray-600 text-sm">Mon-Fri, 9am - 5pm (UK Time)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#05D38A]/5 border-[#05D38A]/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#05D38A]/20 flex items-center justify-center shrink-0">
                      <MessageSquare className="w-5 h-5 text-[#05D38A]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#111827] mb-1">Response Guarantee</h3>
                      <p className="text-gray-600 text-sm">
                        As a professional-led platform, we aim to respond to all resident inquiries within <strong className="text-[#111827]">48 hours</strong>.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Platform</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/succession" className="hover:text-[#05D38A]">Succession Triage</Link></li>
                <li><Link to="/exchange" className="hover:text-[#05D38A]">Mutual Exchange</Link></li>
                <li><Link to="/downsizing" className="hover:text-[#05D38A]">Downsizing</Link></li>
                <li><Link to="/unlock" className="hover:text-[#05D38A]">Right to Buy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/library" className="hover:text-[#05D38A]">Knowledge Hub</Link></li>
                <li><Link to="/about" className="hover:text-[#05D38A]">About Us</Link></li>
                <li><Link to="/providers" className="hover:text-[#05D38A]">For Providers</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/privacy" className="hover:text-[#05D38A]">Privacy & Security</Link></li>
                <li><Link to="/terms" className="hover:text-[#05D38A]">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/contact" className="hover:text-[#05D38A]">Get in Touch</Link></li>
                <li><span>hello@tenures.app</span></li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-8 border-t border-gray-200">
            <TenUResLogo size="text-xl" />
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} TenURes. Advisory tool for social housing residents.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
